import { useState } from 'react'
import { Icon } from '../../components/Brand'
import { money, request, shortDate, titleize } from '../api'
import useResource from '../useResource'
import { Badge, MetricCard, PageHeader, StatePanel } from './Common'

const stages = ['threshold_crossed', 'internal_escalation', 'legal_review', 'cease_notice_issued', 'resolved']
const approvalLabels = { coo: 'COO', general_counsel: 'General Counsel', pnl_manager: 'P&L Manager', collections_manager: 'Collections Manager' }

function stageRows(payload, stage) {
  const value = payload?.[stage] ?? payload?.[titleize(stage)] ?? []
  return Array.isArray(value) ? value : value?.items || []
}

function AccountDrawer({ account, onClose, onChanged, onNavigate }) {
  const activity = useResource(`escalations/${account.customer_id}/timeline`)
  const [working, setWorking] = useState(null)
  const updateApproval = async (role, approved) => {
    setWorking(role)
    try {
      await request(`escalations/${account.customer_id}/approval`, { method: 'PATCH', body: { role, approved } })
      onChanged()
    } finally { setWorking(null) }
  }
  const approvals = account.approvals || {}
  return <div className="col-drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <aside className="col-drawer">
      <header><div><span className="col-eyebrow">Escalation account</span><h2>{account.customer_name || account.name || account.customer_id}</h2><p>{titleize(account.business_unit)} · {money(account.open_amount_nis || account.amount_nis)}</p></div><button className="col-icon-btn" onClick={onClose}><Icon name="close" /></button></header>
      <section><h3>Approval status</h3><div className="col-approval-list">{Object.entries(approvalLabels).map(([role, label]) => {
        const approved = approvals[role] === true
        return <div key={role}><span><i className={approved ? 'approved' : ''} /><b>{label}</b></span><button disabled={working === role} className={`col-mini-action ${approved ? 'approved' : ''}`} onClick={() => updateApproval(role, !approved)}>{approved ? 'Approved' : 'Mark approved'}</button></div>
      })}</div></section>
      <section><h3>Timeline</h3><StatePanel {...activity} empty={activity.data && !activity.data.length} onRetry={activity.retry} emptyTitle="No escalation activity yet"><div className="col-timeline">{(activity.data || []).slice(0, 12).map((entry, index) => <div key={entry.id || index}><i /><span><b>{entry.summary || entry.action || titleize(entry.entry_type || entry.type)}</b><small>{entry.actor || 'System'} · {shortDate(entry.created_at || entry.timestamp)}</small></span></div>)}</div></StatePanel></section>
      <footer><button className="col-btn secondary" onClick={() => onNavigate('collections-customer', { customerId: account.customer_id })}>Open Customer 360</button></footer>
    </aside>
  </div>
}

export default function Escalations({ onNavigate, businessUnit = '' }) {
  const resource = useResource('escalations', { business_unit: businessUnit })
  const [selected, setSelected] = useState(null)
  const [moving, setMoving] = useState(null)
  const data = resource.data
  const move = async (event, account) => {
    event.stopPropagation()
    const stage = event.target.value
    setMoving(account.customer_id)
    try { await request(`escalations/${account.customer_id}/stage`, { method: 'PATCH', body: { stage } }); resource.retry() }
    finally { setMoving(null) }
  }
  return <div className="col-page col-page-wide">
    <PageHeader title="Escalations" description="Track high-risk accounts through internal and legal review." />
    <StatePanel {...resource} empty={data && !Object.values(data.stages || {}).some((rows) => rows?.length)} onRetry={resource.retry} emptyTitle="No accounts in escalation">
      {data && <>
        <div className="col-metric-grid three">
          <MetricCard label="In escalation" value={data.kpis?.in_escalation} icon="crisis_alert" />
          <MetricCard label="Pending legal approval" value={data.kpis?.pending_legal_approval} icon="gavel" tone="peach" />
          <MetricCard label="Problematic accounts" value={data.kpis?.flagged_as_problematic} icon="flag" tone="lav" />
        </div>
        <div className="col-kanban">{stages.map((stage, stageIndex) => {
          const rows = stageRows(data.stages, stage)
          return <section className="col-kanban-col" key={stage}>
            <header><span><i data-stage={stageIndex} />{titleize(stage)}</span><b>{rows.length}</b></header>
            <div>{rows.map((account) => <article className="col-account-card" key={account.customer_id} onClick={() => setSelected(account)}>
              <div><Badge tone={account.collection_status === 'dispute' || account.collection_status === 'off_boarding' ? 'danger' : 'neutral'}>{titleize(account.collection_status || account.status || stage)}</Badge><button className="col-icon-btn"><Icon name="more_horiz" /></button></div>
              <h3>{account.customer_name || account.name || account.customer_id}</h3>
              <p>{titleize(account.business_unit)}</p>
              <strong>{money(account.open_amount_nis || account.amount_nis)}</strong>
              <small>{account.days_overdue || account.age || 0} days overdue</small>
              <select value={account.stage || stage} disabled={moving === account.customer_id} onClick={(event) => event.stopPropagation()} onChange={(event) => move(event, account)}>{stages.map((value) => <option key={value} value={value}>{titleize(value)}</option>)}</select>
            </article>)}{!rows.length && <div className="col-kanban-empty">Drop accounts here</div>}</div>
          </section>
        })}</div>
      </>}
    </StatePanel>
    {selected && <AccountDrawer account={selected} onClose={() => setSelected(null)} onChanged={() => { resource.retry(); setSelected(null) }} onNavigate={onNavigate} />}
  </div>
}
